/* eslint-disable no-unused-vars */
import { CONFIG_API, DATA_API } from "@/plugins/axios";
import moment from "moment";

const baseDates = {
    startOfTheYear: "YYYY-01-01T00:00:00",
    startOfTheMonth: "YYYY-MM-01T00:00:00",
    startOfTheDay: "YYYY-MM-DDT00:00:00",
    now: "YYYY-MM-DDTHH:mm:ss"
};

const baseIntervals = {
    "10minute": 600000,
    "15minute": 900000,
    hour: 3600000,
    day: 86400000,
    month: 86400000,
    oneDay: 86400000,
    oneHour: 3600000
};

const defaultSource = "EPM";

export default class HistoricalMeasure {
    /**
   * @param {Object} item Historical Measurement
   */
    constructor(item) {
        if (item) {
            this.name = item.name;
            this.nameRef = item.nameRef;

            this.node = item.node;
            this.aggregation = item.aggregation;
            this.highGranularity = item.high_granularity ? item.high_granularity : false;
            this.highGranularity = item.highGranularity ? item.highGranularity : false;
            this.treatGeneratedDataAsNull = item.treat_generated_data_as_null
                ? item.treat_generated_data_as_null
                : item.treatGeneratedDataAsNull
                    ? item.treatGeneratedDataAsNull
                    : false;
            this.checkQuality = item.checkQuality ? item.checkQuality : false;
            if (item.dataSource) {
                this.dataSource = item.dataSource;
            } else {
                if (item.data_source) {
                    this.dataSource = item.data_source;
                } else {
                    this.dataSource = defaultSource;
                }
            }
            this.values = [];
            this.data = [];

            this.description = item.description;
            this.eu = item.eu;
            this.decimalPlace = item.decimalPlace ? item.decimalPlace : 2;
            this.multiplier = item.multiplier ? parseFloat(item.multiplier) : 1;

            this.plant = item.plant;
            this.equipment = item.equipment;

            this.sum = false;
            this.start = moment().format(baseDates.startOfTheMonth);
            this.end = moment().format(baseDates.now);
            this.interval = baseIntervals.oneDay;
            this.offset = new Date().getTimezoneOffset() / 60 * -1
            this.source = null;
            this.token = null;
        }
    }

    setAttributes(historicalRef) {
        const { name, multiplier, description, eu, decimalPlace, dataSource } = historicalRef;
        if (multiplier) this.multiplier *= parseFloat(multiplier);
        if (description) this.description = description;
        if (eu) this.eu = eu;
        // if (dataSource) this.dataSource = dataSource;
        if (typeof decimalPlace === "number" && decimalPlace >= 0) this.decimalPlace = decimalPlace;
        if (name) {
            this.nameRef = this.name;
            this.name = name;
        }
        return this;
    }

    setValues(values) {
        this.values = values;
        this.data = this.getDataset(e => ({ x: e.timestamp, y: e.value }));
    }

    isDataFrom(value) {
        return this.dataSource.toLowerCase().includes(value.toLowerCase());
    }

    isEPM() {
        return this.isDataFrom("EPM");
    }

    forEach(callback = (element, index, array) => { }) {
        for (let i = 0; i < this.values.length; i++) {
            const element = this.values[i];
            callback(element, i, this.values);
        }
    }

    copy() {
        return new HistoricalMeasure({ ...this });
    }

    assignCopy(attributes = {}) {
        return new HistoricalMeasure(Object.assign({ ...this }, attributes));
    }

    label() {
        if (!this.eu) return this.description;
        return `${this.description} (${this.eu})`;
    }

    dataset() {
        return this.getDataset(e => {
            let value = null;

            if (e.value || e.value === 0) {
                value = Number(e.value);
            }

            return {
                x: e.timestamp,
                y: value
            };
        });
    }

    getEu() {
        return this.eu ? this.eu : "";
    }

    applyMultiplier(data) {
        if (isNaN(this.multiplier)) {
            return data;
        }
        data.forEach(element => {
            const yValue = element.y == null || element.y == undefined ? 'notANumber' : Number(element.y); // Change necessary cause Number() transform null and undefined to zero.
            if (isNaN(yValue)) {                                                                           // And in isNaN() null and undefined are treated as zero
            } else {
                element.y = Number((this.multiplier * yValue).toFixed(this.decimalPlace));
            }
        });
        return data;
    }

    getDataset(callback) {
        let array = [];
        this.values.forEach(item => {
            if (callback) {
                item = callback(item);
                if (item) array.push(item);
            } else {
                const { value, timestamp } = item;
                array.push({
                    x: timestamp,
                    y: value
                });
            }
        });
        array = this.applyMultiplier(array);
        return array;
    }

    validateParams(params) {
        const acceptedParams = ["sum", "start", "end", "interval"];
        if (typeof params !== "object") {
            throw Error(
                `Params must be an object received ${params} \n` +
                `Available params: ${acceptedParams.join(", ")} \n`
            );
        }
        let validated = {};
        acceptedParams.forEach(key => {
            let item = params[key];
            validated[key] = item ? item : this[key];
            this[key] = validated[key];
        });

        return validated;
    }

    async queryData(params) {
        params = this.validateParams(params);
        const isConsolidated = this.isDataFrom("Consolidated");
        const isEPM = this.isDataFrom("EPM");
        let data = [];
        if (isConsolidated) {
            data = await getConsolidatedData(params, this);
        } else if (isEPM) {
            data = await getEPMData(params, this);
        }

        return data;
    }

    async setData(params = {}) {
        this.values = await this.queryData(params);
        this.data = this.getDataset();
        return this.values;
    }

    async setDataMonthly(params = {}) {
        const dateRanges = getDateRanges(params);
        const summation = params.sum;

        const isConsolidated = this.isDataFrom("Consolidated");
        let list = [];

        if (isConsolidated) {
            list = await this.queryData(params);
        } else {
            for (let i = 0; i < dateRanges.length; i++) {
                const { start, end } = dateRanges[i];
                params = { start, end, sum: true };
                let data = await this.queryData(params);
                data = transformDaysToMonth(data);
                list = list.concat(data);
            }
        }

        if (!isConsolidated && summation) {
            list = monthSummation(list);
        }
        this.values = list;
        this.data = this.getDataset();
    }
}

export async function getHistoricalMeasures(params) {
    try {
        const headers = {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Authorization: "JWT " + localStorage.getItem("config_token")
        };
        const config = { headers, params };
        let res = await CONFIG_API.get("historical-measures/", config);
        if (res && res.status == 200) {
            if ("page" in res.data) {
                res.data.results = res.data.results.map(e => new HistoricalMeasure(e));
                return res.data;
            }
            let data = res.data.map(e => new HistoricalMeasure(e));
            return data;
        }
    } catch (e) {
        console.error(e);
        return [];
    }
}

export async function insertHistoricalMeasure(data) {
    try {
        const headers = {
            "Content-Type": "application/json; charset=utf-8",
            Authorization: "JWT " + localStorage.getItem("config_token")
        };
        const config = { headers };
        let res = await CONFIG_API.post("historical-measures/", data, config);
        return res;
    } catch (e) {
        console.error(e);
        return e;
    }
}

// async function getEPMData(params, measurement) {
//   let { start, end, interval, sum } = params;
//   let { node, aggregation, checkQuality, highGranularity, offset } = measurement;

//   if (typeof interval == "string") {
//     interval = baseIntervals[interval];
//   }
//   if (Array.isArray(node)) {
//     node = node.join('","');
//   }
//   const query = `{
//     epm(
//       epmNode:["${node}"]
//       epmStartTime: "${start}"
//       epmEndTime: "${end}"
//       epmSampleInterval: ${interval}
//       epmAggregateFunction:"${aggregation}"
//       cumulativeSum:${sum}
//       treatGeneratedDataAsNull:${checkQuality}
//       highGranularity:${highGranularity}
//       epmOffset:${offset}
//       ){
//       name
//       value,
//       timestamp,
//       quality
//     }
//   }`;
//   let response = await executeQuery(query);
//   if (response && response.epm) {
//     return response.epm;
//   }
//   return [];
// }

async function getEPMData(params, measurement) {
    let { start, end, interval, sum } = params;
    let {
        node,
        aggregation,
        checkQuality,
        highGranularity,
        treatGeneratedDataAsNull,
        offset
    } = measurement;
    if (typeof interval == "string") {
        interval = baseIntervals[interval];
    }
    params = {
        start_time: start,
        end_time: end,
        interval: interval,
        aggregation,
        accum_sum: sum,
        high_granularity: highGranularity,
        treat_generated_data_as_null: treatGeneratedDataAsNull,
        offset
    };
    if (Array.isArray(node)) params.nodes = node;
    else params.node = node;
    try {
        const headers = {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Authorization: "JWT " + localStorage.getItem("data_token")
        };
        const config = { headers, params };
        let res = await DATA_API.get("epm-data/", config);
        if (res && res.status == 200) {
            return res.data;
        }
        return [];
    } catch (e) {
        console.error(e);
        return [];
    }
}

export async function getConsolidatedData(args, measurement) {
    const { start, end, sum } = args;
    let params = {};
    let { node } = measurement;
    params = {
        name: node,
        start_time: start,
        end_time: end,
        accum_sum: sum
    };
    if (args.page && args.page_size) {
        params["page"] = args.page;
        params["page_size"] = args.page_size;
    }
    try {
        const headers = {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Authorization: "JWT " + localStorage.getItem("data_token")
        };
        const config = { headers, params };
        let res = await DATA_API.get("consolidated-data/", config);
        if (res && res.status == 200) {
            return res.data;
        }
        return [];
    } catch (e) {
        console.error(e);
        return [];
    }
}
/**
 * Get all pathnames in consolidated that matches, with likewise, the params passed.
 * @param {Object} params object with a attribute name
 * where name is a String or an Array
 * @returns {Array} list of all pathnames available in consolidated for given params
 */
export async function getConsolidatedMeasurements(params) {
    try {
        const headers = {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Authorization: "JWT " + localStorage.getItem("data_token")
        };
        const config = { headers, params };
        let res = await DATA_API.get("consolidated-measurements/", config);
        if (res && res.status === 200) {
            return res.data;
        }
    } catch (error) {
        return Promise.reject(error);
    }
    return [];
}

// async function getConsolidatedData(params, measurement) {
//   const { start, end, sum } = params;
//   let { node } = measurement;

//   if (Array.isArray(node)) {
//     node = node.join('","');
//   }

//   const query = `{
//     historicalData(
//       pathname:["${node}"]
//       startTime: "${start}"
//       endTime: "${end}"
//       cumulativeSum: ${sum}
//       ){
//       name
//       value,
//       timestamp
//     }
//   }`;
//   let response = await executeQuery(query);
//   if (response && response.historicalData) {
//     return response.historicalData;
//   }
//   return [];
// }

// async function executeQuery(query) {
//   try {
//     const config = {
//       headers: {
//         Authorization: "JWT " + localStorage.getItem("data_token")
//       }
//     };
//     let response = await EPMAPI.post("", { query }, config);
//     response = response.data;
//     if (response.data) {
//       return response.data;
//     }
//   } catch (error) {
//     console.error(error);
//     return error;
//   }
// }

function getDateRanges(params) {
    let dates = [];
    let { start, end } = params;
    end = moment(end);

    for (let m = moment(start); end.diff(m, "month") > 0; m.add(1, "month")) {
        let endDate = m.endOf("month").format("YYYY-MM-DDTHH:mm:ss");
        let startDate = m.startOf("month").format("YYYY-MM-DDTHH:mm:ss");
        dates.push({
            start: startDate,
            end: endDate
        });
    }
    dates.push({
        end: end.format("YYYY-MM-DDTHH:mm:ss"),
        start: end.startOf("month").format("YYYY-MM-DDTHH:mm:ss")
    });

    return dates;
}

function getMatrixGroupedByKey(data, wantedKey = "name") {
    let keys = new Set();
    data.forEach(e => keys.add(e[wantedKey]));

    let matrix = [];
    keys.forEach(key => {
        let filtred = data.filter(e => e[wantedKey] === key);
        matrix.push(filtred);
    });

    return matrix;
}

function transformDaysToMonth(data, params) {
    if (!data || !data.length) return [];

    let matrix = getMatrixGroupedByKey(data);

    let monthlyData = [];
    matrix.forEach(array => {
        let item = array.reduce((a, b) => {
            b.timestamp = a.timestamp;
            return b;
        });
        monthlyData.push(item);
    });

    return monthlyData;
}

function monthSummation(list) {
    let matrix = getMatrixGroupedByKey(list);
    let newList = [];
    matrix.forEach(array => {
        let aux;
        array.forEach(item => {
            if (aux) {
                item.value += aux.value;
            }
            aux = item;
            newList.push(item);
        });
    });
    return newList;
}
